import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { TourItem } from './index';

export const TOUR_STEP_BORDER_WIDTH = 3;
export const SIZE_OFFSET = TOUR_STEP_BORDER_WIDTH * 4;

export function TourStep({
  interactive = true,
  onTop = false,
  onRight = false,
  animateTransition = true,
  ...props
}: TourItem & {
  stepIndex: number;
  component: HTMLElement | null;
}) {
  const [stepHint, setStepHint] = useState(false);
  const stepHintRef = useRef<NodeJS.Timeout | null>(null);

  const rect: DOMRect | undefined = props.component?.getBoundingClientRect();

  const top = (rect?.top ?? 0) - SIZE_OFFSET / 2;
  const left = (rect?.left ?? 0) - SIZE_OFFSET / 2;

  const isFullScreen = top <= 0 && left <= 0;

  useEffect(() => {
    if (stepHintRef.current && !stepHint) {
      clearTimeout(stepHintRef.current);
      stepHintRef.current = null;
      return;
    }

    stepHintRef.current = setTimeout(() => {
      setStepHint(false);
    }, 400);
  }, [stepHint]);

  return rect ? (
    <>
      <div
        className={classNames('absolute border-gray-200/50 box-border')}
        style={{
          top: !isFullScreen ? top : 0,
          left: !isFullScreen ? left : 0,
          zIndex: 50,
          borderWidth: TOUR_STEP_BORDER_WIDTH,
          padding: '3px',
          width: !isFullScreen ? rect?.width + SIZE_OFFSET : rect.width,
          height: !isFullScreen ? rect?.height + SIZE_OFFSET : rect.height,
        }}
      />
      <div
        className={classNames(
          'absolute bg-white w-content max-w-[70%] px-3 rounded-md py-3 pointer-events-auto border-2 shadow-md',
          stepHint ? 'border-blue' : 'border-gray-200',
          top <= 0 && 'animate-small-bounce !shadow-xl',
          animateTransition && 'transition-all',
        )}
        style={{
          top: !isFullScreen ? (onTop ? rect.top : rect?.bottom + 8) : '65%',
          left: !isFullScreen ? (onRight ? 'auto' : rect?.left - 4) : '6%',
          marginTop: '5px',
          right: onRight ? rect.left : undefined,
          marginBottom: onTop ? '6rem' : undefined,
          zIndex: 55,
        }}
        data-test-id="guided-tour-step"
      >
        <p className="text-lg font-medium pointer-events-none selection:bg-transparent">
          {props.title}
        </p>
        {!!props.description && <hr className="mt-2" />}
        {!!props.description &&
          (typeof props.description === 'string' ? (
            <p className="py-2 text-gray-600 w-full pointer-events-none selection:bg-transparent">
              {props.description}
            </p>
          ) : (
            props.description
          ))}
        {props.action && (
          <div className="w-full flex justify-end mt-2">
            <button
              data-test-id="tour-next-button"
              className={classNames(
                'bg-blue text-white rounded-md border border-blue shadow-sm px-3 py-1 transition-colors',
              )}
              onClick={props.action}
            >
              Next
            </button>
          </div>
        )}
      </div>
      <div
        className={classNames(
          'absolute border box-border rounded-md animate-pulse box-border',
          interactive ? 'pointer-events-none' : 'pointer-events-auto',
        )}
        onClick={() => {
          setStepHint(true);
        }}
        id="onboarding-component"
        style={{
          top: !isFullScreen ? top : 0,
          left: !isFullScreen ? left : 0,
          zIndex: 50,
          padding: `${TOUR_STEP_BORDER_WIDTH}px`,
          border: `${TOUR_STEP_BORDER_WIDTH}px solid blue`,
          width: !isFullScreen ? rect?.width + SIZE_OFFSET : rect.width,
          height: !isFullScreen ? rect?.height + SIZE_OFFSET : rect.height,
        }}
      />
    </>
  ) : null;
}
